// use-auth-guard v2: 通用鉴权守卫 Hook（第三阶段引入 2026-04-17）
// ============================================================================
// 用途：保护"需要登录的写操作"（点赞、评论、关注、收藏、发布、合作申请等）
//
// 使用方式（两种入口）：
//
//   const {
//     guard, requireLogin,
//     isLoginModalOpen, handleLoginSuccess, handleClose,
//   } = useAuthGuard();
//
//   // 方式一：guard(fn) —— 适合"handler 完全由当前组件控制且无内部静默拦截"的场景
//   <button onClick={() => guard(handleLike)}>Like</button>
//
//   // 方式二：requireLogin() —— 返回 boolean，适合"下级 hook 内部已做 !isLoggedIn return"的场景
//   //         这种场景下自动重放会被下级 hook 的旧闭包 early return 吞掉，不能依赖 pending 重放
//   <button
//     onClick={() => {
//       if (!requireLogin()) return;
//       likeCountState.toggleLike();
//     }}
//   >Like</button>
//
//   <LoginModal
//     isOpen={isLoginModalOpen}
//     onClose={handleClose}
//     onLoginSuccess={handleLoginSuccess}
//   />
//
// 行为：
//   - guard(fn)：已登录立即执行；未登录弹窗 + 记下 fn，登录成功后自动重放
//   - requireLogin()：已登录返回 true；未登录弹窗并返回 false（无重放，需用户再次点击）
//   - 点 Cancel → 取消 pending 操作并关闭弹窗
// ============================================================================

import { useState, useCallback, useEffect, useRef } from 'react';

import { useAuth } from '../context/AuthContext';

/**
 * 鉴权守卫 Hook：保护需要登录才能执行的操作
 *
 * @returns {Object} 守卫相关的状态和操作
 *   - guard: (actionFn) => void    异步守卫：未登录时记下 fn，登录后自动重放
 *   - requireLogin: () => boolean  同步守卫：未登录时弹窗并返回 false（无重放）
 *   - isLoginModalOpen: boolean    是否应显示登录弹窗
 *   - handleLoginSuccess: () =>    登录成功回调（传给 LoginModal.onLoginSuccess）
 *   - handleClose: () =>           弹窗关闭回调（传给 LoginModal.onClose）
 */
const useAuthGuard = () => {
  const { user } = useAuth();

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // 使用 useRef 存储 pending action，避免闭包陈旧并防止不必要的 re-render
  const pendingActionRef = useRef(null);

  /**
   * 守卫包装器：传入需要鉴权的操作函数，已登录直接执行，未登录弹登录窗
   * @param {Function} actionFn 实际要执行的操作
   */
  const guard = useCallback(
    actionFn => {
      if (typeof actionFn !== 'function') {
        console.warn('[useAuthGuard] guard() expects a function argument');
        return;
      }

      if (user) {
        // 已登录，直接执行
        actionFn();
        return;
      }

      // 未登录，记下待执行的操作并弹出登录窗
      pendingActionRef.current = actionFn;
      setIsLoginModalOpen(true);
    },
    [user]
  );

  /**
   * 同步守卫：检查登录状态
   * @returns {boolean} 已登录返回 true；未登录弹窗并返回 false（不存 pending action，不重放）
   *
   * 适用于下级 hook 已有 `!isLoggedIn return` 静默失败分支的场景：
   * 此时自动重放会因旧闭包的 isLoggedIn=false 被吞掉，所以只弹窗不重放，
   * 由用户登录成功后自行再次点击按钮。
   */
  const requireLogin = useCallback(() => {
    if (user) return true;
    setIsLoginModalOpen(true);
    return false;
  }, [user]);

  /**
   * 登录成功回调：传给 LoginModal.onLoginSuccess
   * 注意：此时 user state 可能尚未同步更新（React setState 异步），
   * 因此不在此处直接调用 pendingAction，而是由下方 useEffect 监听 user 变化统一处理
   */
  const handleLoginSuccess = useCallback(() => {
    setIsLoginModalOpen(false);
    // pendingActionRef 保留不清空，交给 useEffect 在 user 更新后消费
  }, []);

  /**
   * 弹窗关闭回调：用户主动取消
   */
  const handleClose = useCallback(() => {
    setIsLoginModalOpen(false);
    pendingActionRef.current = null;
  }, []);

  // 监听 user 变化：当用户从 null → 有值、且存在 pending action 时自动执行
  // 这样可以规避 LoginModal.onLoginSuccess 触发时 user state 尚未更新的闭包陷阱
  useEffect(() => {
    if (user && pendingActionRef.current) {
      const fn = pendingActionRef.current;
      pendingActionRef.current = null;
      try {
        fn();
      } catch (error) {
        console.error('[useAuthGuard] pending action threw:', error);
      }
    }
  }, [user]);

  return {
    guard,
    requireLogin,
    isLoginModalOpen,
    handleLoginSuccess,
    handleClose,
  };
};

export default useAuthGuard;
